import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import {
  loadRemoteComponents,
  removeRemoteComponents,
  Environment,
} from '@oatfi/remote-loader';

import WarningDialog from 'components/dialog/components/WarningDialog';
import { openDialog } from 'components/dialog/modalSlice';
import { useAppDispatch, useAppSelector } from 'hooks';
import { getBusiness } from 'pages/Profile/selectors';
import { getOatFiToken } from 'services/integrations';
import { isUATDomain } from 'utils/domainService';
import { LoaderIcon } from 'components/icons';
import Modal from 'components/Modal';

import { setShowOatFiOnboardingModal } from '../submitDashboardSlice';
import { oatFiOnboarding } from '../thunks';

type OatFiOnboardingProps = {
  onClose: () => void;
};

const OatFiOnboardingModal = ({ onClose }: OatFiOnboardingProps) => {
  const { t } = useTranslation('dashboard');
  const dispatch = useAppDispatch();

  const [partnerAuthToken, setPartnerAuthToken] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const business = useAppSelector(getBusiness);

  const handleErrors = () => {
    setIsLoading(false);

    dispatch(
      openDialog({
        hideCross: false,
        isOverlayDisabled: false,
        maxWidth: '450px',
        content: (
          <WarningDialog
            title={t('dashboard:submitModal.lineOfCredit.errorTitle')}
          />
        ),
      }),
    );

    dispatch(setShowOatFiOnboardingModal(false));
  };

  useEffect(() => {
    const ensureBusinessCreated = async () => {
      if (business.oatFiBusinessExternalId) return;

      const result = await dispatch(oatFiOnboarding());

      if (result.payload == null) {
        handleErrors();
      }
    };

    ensureBusinessCreated();

    return () => {
      removeRemoteComponents('oatfi-onboarding');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const getToken = async () => {
      if (!business.oatFiBusinessExternalId) return;

      const authToken = await getOatFiToken();

      if (authToken) {
        setPartnerAuthToken(authToken);
      } else {
        handleErrors();
      }
    };

    getToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business.oatFiBusinessExternalId]);

  useEffect(() => {
    if (!partnerAuthToken) return;

    const loadComponent = async () => {
      try {
        await loadRemoteComponents({
          token: partnerAuthToken,
          scriptId: 'oatfi-onboarding',
          environment: isUATDomain() ? Environment.STG : Environment.PROD,
        });

        setIsLoading(false);
      } catch (error) {
        handleErrors();
      }
    };

    loadComponent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerAuthToken]);

  return (
    <Modal
      isOpen={true}
      title={isLoading ? '' : t('submitModal.lineOfCredit.connectOatFiTitle')}
      closeModal={onClose}
      maxWidth={isLoading ? '450px' : '1192px'}
    >
      {isLoading ? (
        <div className="p-8">
          <LoaderIcon className="w-11 h-11 mx-auto" />
          <p className="text-center mb-2 mt-6 text-gray-900 font-semibold text-lg">
            {t('submitModal.lineOfCredit.loadingTitle')}
          </p>
          <p className="text-center text-sm text-gray-600">
            {t('submitModal.lineOfCredit.loadingText')}
          </p>
        </div>
      ) : (
        <div className="p-6">
          {partnerAuthToken && (
            <oatfi-onboarding
              token={partnerAuthToken}
              businessExternalId={business.oatFiBusinessExternalId}
              environment={isUATDomain() ? Environment.STG : Environment.PROD}
              lmsProgramId={business.oatFiUnderwritingProductId}
            />
          )}
        </div>
      )}
    </Modal>
  );
};

export default OatFiOnboardingModal;
